import type { ClassificationContent } from '@classification-testing/domain';
import { ContentType } from '@classification-testing/domain';
import type { DomainError } from '@core/domain';
import { AudioContentRenderer, ImageContentRenderer, FileUploadCard } from '@soteria-ui/src/classification-testing/components';

interface UploadTabContentProps {
  content: ClassificationContent | null;
  pendingFile: File | null;
  errors: DomainError[] | null;
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onClear: () => void;
  onFileSelect: (file: File) => void;
  onFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPaste: (event: React.ClipboardEvent) => void;
}

interface UploadedContentProps {
  content: ClassificationContent | null;
  pendingFile: File | null;
  isUploading: boolean;
  hasError: boolean;
  onClear: () => void;
}

function isAudioUpload(content: ClassificationContent | null, pendingFile: File | null): boolean {
  return !!(pendingFile?.type.startsWith('audio/') || content?.type === ContentType.AUDIO);
}

function isImageUpload(content: ClassificationContent | null): boolean {
  return content?.type === ContentType.IMAGE;
}

function renderUploadedContent(props: UploadedContentProps) {
  const { content, pendingFile, isUploading, hasError, onClear } = props;

  if (isAudioUpload(content, pendingFile)) {
    return <AudioContentRenderer audioUrl={content?.audioUrl || ''} hasError={hasError} isLoading={isUploading} onClear={onClear} />;
  }

  if (isImageUpload(content)) {
    return <ImageContentRenderer hasError={hasError} imageUrl={content?.imageUrl || ''} isLoading={isUploading} onClear={onClear} />;
  }

  return null;
}

/**
 * UploadTabContent component to render upload tab content
 * Extracts upload tab logic from TabContent component
 */
export function UploadTabContent({
  content,
  pendingFile,
  errors,
  isUploading,
  fileInputRef,
  onClear,
  onFileSelect,
  onFileInputChange,
  onPaste,
}: UploadTabContentProps) {
  const hasError = !!(errors && errors.length > 0);
  const hasUploadedContent =
    isUploading || (content?.type === ContentType.IMAGE && content.imageUrl) || (content?.type === ContentType.AUDIO && content.audioUrl);

  if (hasUploadedContent) {
    return <div>{renderUploadedContent({ content, pendingFile, isUploading, hasError, onClear })}</div>;
  }

  return (
    <FileUploadCard
      isActive
      fileInputRef={fileInputRef}
      hasError={hasError}
      onFileInputChange={onFileInputChange}
      onFileSelect={onFileSelect}
      onPaste={onPaste}
    />
  );
}
